/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface EmspTariffsRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
  counterPartyId?: string;
  counterCountryCode?: string;
}

interface FetchEmspTariffsResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches a page of OCPI tariffs received from connected CPOs for an eMSP role.
 */
export const fetchEmspTariffs = createAsyncThunk<FetchEmspTariffsResponse, EmspTariffsRequest>(
  'emsp-tariffs/fetchEmspTariffs',
  async (
    { countryCode, partyId, counterPartyId, counterCountryCode, page, limit },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalEmspTariffs(
        countryCode,
        partyId,
        counterPartyId || '',
        counterCountryCode || '',
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: resolvedPage,
      };
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch received tariffs.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Builds a user-facing error message for a failed pull request, distinguishing between
 * being offline, the partner not responding, and the partner returning an error response.
 * @param {any} error - The error thrown by the pull request.
 * @returns {string} A user-facing error message.
 */
const resolvePullErrorMessage = (error: any): string => {
  if (typeof window !== 'undefined' && !window.navigator.onLine) {
    return 'You appear to be offline. Check your internet connection and try again.';
  }
  if (error?.response) {
    return (
      error.response.data?.message ||
      error.response.data?.status_message ||
      'Failed to pull tariffs from the connected CPO.'
    );
  }
  if (error?.request) {
    return 'No response received from the connected CPO. Please try again later.';
  }
  return error?.message || 'Failed to pull tariffs from the connected CPO.';
};

/**
 * Manually pulls OCPI tariffs from a connected CPO partner for an eMSP role.
 */
export const pullEmspTariffs = createAsyncThunk<
  { message: string },
  {
    countryCode: string;
    partyId: string;
    counterPartyId?: string;
    counterCountryCode?: string;
    connectionId?: string;
  }
>(
  'emsp-tariffs/pullEmspTariffs',
  async (
    { countryCode, partyId, counterPartyId, counterCountryCode, connectionId },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await ocpiApi.pullInternalEmspTariffs(
        countryCode,
        partyId,
        counterPartyId,
        counterCountryCode,
        connectionId,
      );
      const message =
        response?.data?.message ||
        response?.data?.status_message ||
        'Tariffs successfully pulled.';
      return { message };
    } catch (error: any) {
      const message = resolvePullErrorMessage(error);
      notify(dispatch, message, 'error');
      return rejectWithValue(message);
    }
  },
);

/**
 * Resolves the filter list for the eMSP tariffs table. Filter options are known upfront
 * (there is no backend filter-list endpoint for OCPI tariffs), so this only exists to
 * satisfy CustomTable's filterListToDispatch contract.
 */
export const fetchEmspTariffFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'emsp-tariffs/fetchEmspTariffFilters',
  async () => availableFilters,
);

interface EMSPTariffsInitialState {
  tariffs: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

const initialState: EMSPTariffsInitialState = {
  tariffs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const emspTariffsSlice = createSlice({
  name: 'emsp-tariffs',
  initialState,
  reducers: {
    /**
     * Update eMSP Tariffs filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateEMSPTariffsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableEmspTariffsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmspTariffs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmspTariffs.fulfilled, (state, action) => {
        state.loading = false;
        state.tariffs = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchEmspTariffs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchEmspTariffFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchEmspTariffFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchEmspTariffFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateEMSPTariffsFilters, disableEmspTariffsFilterApplied, setSortData } =
  emspTariffsSlice.actions;

export default emspTariffsSlice.reducer;
